import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  i18n,
  setGlobalLoading,
  showMessage,
  useAppDispatch
} from 'src/common';
import { useGetOfferForAgentQuery, useHealthQuestionnaireQuestionsQuery, useSaveAndSubmitHealthQuestionnaireForAdminMutation } from 'src/common/api/apollo/additional';
import { Flex, Loader } from 'src/components';
import { buildInitialQuestionnaireValuesFromOffer } from 'src/components/forms/healthQuestionnaire/buildInitialQuestionnaireValuesFromOffer.util';
import {
  buildSubmitAnswerInputs,
  collectFollowUpParentMap,
} from 'src/components/forms/healthQuestionnaire/buildSaveAnswerInputs.util';
import { HealthQuestionnaireForm } from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.form';
import {
  type AnyQuestion,
  type QuestionnaireAnswers,
} from 'src/components/forms/healthQuestionnaire/healthQuestionnaire.types';
import config from 'src/config';
import { AppRoutesAdditional } from '../../_router/app.routes';

const HealthQuestionnaireAgentPage = () => {
  const dispatch = useAppDispatch();
  const params = useParams<{ offerId: string, adminId: string }>();
  const { data: getOfferAdditionalData, loading: loadingOfferAdditional } = useGetOfferForAgentQuery({
    variables: { id: params.offerId || '', adminId: params.adminId || '' },
    skip: !params.offerId || !params.adminId,
    onError: () => {
      window.parent.postMessage({ type: 'HEALTH_QUESTIONNAIRE', status: 'error', message: "Došlo je do greške prilikom dohvaćanja ponude" }, config.REACT_APP_TARGET_ORIGIN);
    }
  });

  const { data: getHealthQuestionnaireQuestionsData, loading: loadingHealthQuestionnaireQuestions } = useHealthQuestionnaireQuestionsQuery({
    variables: { brandCode: getOfferAdditionalData?.getOfferForAgent?.product?.brand?.code ?? '', productCode: getOfferAdditionalData?.getOfferForAgent?.product?.code ?? '', dontExclude: true },
    skip: !getOfferAdditionalData?.getOfferForAgent?.product?.brand?.code || !getOfferAdditionalData?.getOfferForAgent?.product?.code,
    onError: () => {
      window.parent.postMessage({ type: 'HEALTH_QUESTIONNAIRE', status: 'error', message: "Došlo je do greške prilikom dohvaćanja pitanja za zdravstveni upitnik" }, config.REACT_APP_TARGET_ORIGIN);
    },
    onCompleted: (data) => {
      if (data.healthQuestionnaireQuestions.length === 0) {
        window.parent.postMessage({ type: 'HEALTH_QUESTIONNAIRE', status: 'error', message: "Ne postoji zdravstveni upitnik za ovaj proizvod" }, config.REACT_APP_TARGET_ORIGIN);
      }
    }
  });
  const [saveAndSubmitHealthQuestionnaireForAdmin] = useSaveAndSubmitHealthQuestionnaireForAdminMutation(
    {
      onCompleted: () => {
        window.parent.postMessage({ type: 'HEALTH_QUESTIONNAIRE', status: 'success', message: "Zdravstveni upitnik je uspješno spremljen" }, config.REACT_APP_TARGET_ORIGIN);
        dispatch(setGlobalLoading(false));
      },
      onError: () => {
        window.parent.postMessage({ type: 'HEALTH_QUESTIONNAIRE', status: 'error', message: "Došlo je do greške prilikom spremanja zdravstvenog upitnika" }, config.REACT_APP_TARGET_ORIGIN);
        dispatch(setGlobalLoading(false));
      }
    }
  );

  const questionsList = getHealthQuestionnaireQuestionsData?.healthQuestionnaireQuestions;

  const followUpParentMap = useMemo(
    () => collectFollowUpParentMap((questionsList ?? []) as AnyQuestion[]),
    [questionsList],
  );

  const brandCode = getOfferAdditionalData?.getOfferForAgent?.product?.brand?.code ?? '';

  const initialValues = useMemo(
    () =>
      buildInitialQuestionnaireValuesFromOffer(
        (questionsList ?? []) as AnyQuestion[],
        getOfferAdditionalData?.getOfferForAgent?.productTypeSpecific?.healthQuestionnaire ?? null,
      ),
    [questionsList, getOfferAdditionalData?.getOfferForAgent?.productTypeSpecific?.healthQuestionnaire],
  );

  const handleConfirm = useCallback(
    (answers: QuestionnaireAnswers) => {
      const offerId = params.offerId || '';
      const adminId = params.adminId || '';
      if (!offerId || !adminId) return;
      if (!brandCode) {
        dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 5000 }));
        dispatch(setGlobalLoading(false));
        return;
      }
      const answerItems = buildSubmitAnswerInputs(initialValues, answers, followUpParentMap);
      saveAndSubmitHealthQuestionnaireForAdmin({
        variables: {
          id: offerId,
          adminId,
          input: { brandCode, answers: answerItems },
        },
      });
    },
    [params.offerId, params.adminId, brandCode, initialValues, followUpParentMap, dispatch, saveAndSubmitHealthQuestionnaireForAdmin],
  );

  if (loadingOfferAdditional || loadingHealthQuestionnaireQuestions) return <Loader page />;
  if (!getOfferAdditionalData || !getHealthQuestionnaireQuestionsData) return null;

  return (
    <Flex vertical gap={24}>
      <HealthQuestionnaireForm
        questions={getHealthQuestionnaireQuestionsData.healthQuestionnaireQuestions}
        initialValues={initialValues}
        onConfirm={handleConfirm}
        backPath={AppRoutesAdditional.OFFERS_PREVIEW.fullPath}
        admin={true}
      />
    </Flex>
  );
};

export default HealthQuestionnaireAgentPage;